import { Typography, Grid } from "@mui/material";
import { ReactNode, useMemo } from "react";
import "../components-styles.scss";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { phoneRegExp, emailRegExp } from "../../consts";
import { Contact } from "../../types/ClientSpace";
import InputField from "../UIElements/InputField";

type EstablishmentContactFormProps = {
    profilId: string;
    jobTitle?: string;
    contacts: Contact[];
    onSubmit: (data: any) => void;
    formActionComponents: {
        submit: ReactNode;
        cancel?: ReactNode;
    };

    className?: string;
    classes?: any;
};

const validationSchema = yup.object({
    firstName: yup.string().required("Veuillez renseigner le prénom"),
    lastName: yup.string().required("Veuillez renseigner le nom"),
    email: yup
        .string()
        .email("Veuillez renseigner une adresse email valide")
        .matches(emailRegExp)
        .required("Veuillez renseigner une adresse email"),
    phoneNumber: yup
        .string()
        .matches(
            phoneRegExp,
            "Veuillez renseigner un numéro de téléphone valide"
        )
        .required("Veuillez renseigner un numéro de téléphone"),
});

export default function EstablishmentContactFormWrapper({
    profilId,
    jobTitle,
    onSubmit,
    contacts,
    formActionComponents,
    className,
    classes,
}: EstablishmentContactFormProps) {
    const concernedContact: Contact | undefined = useMemo(() => {
        return contacts.find(
            (contact: Contact) => contact.profil + "" === profilId
        );
    }, [contacts, profilId]);

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm({
        defaultValues: {
            firstName: concernedContact?.firstName ?? "",
            lastName: concernedContact?.lastName ?? "",
            email: concernedContact?.email ?? "",
            phoneNumber: concernedContact?.phoneNumber ?? "",
        },
        resolver: yupResolver(validationSchema),
    });

    const _onSubmit = (data: any) => {
        let newContacts = contacts;
        const concernedContactIndex = newContacts.findIndex(
            (c) => c.profil + "" === profilId
        );
        if (concernedContactIndex !== -1) {
            newContacts[concernedContactIndex] = {
                ...newContacts[concernedContactIndex],
                ...data,
                profil: Number(profilId),
            };
        } else {
            newContacts.push({
                ...newContacts[concernedContactIndex],
                ...data,
                profil: Number(profilId),
            });
        }
        onSubmit({ contacts: newContacts });
    };

    return (
        <form
            onSubmit={handleSubmit(_onSubmit)}
            className={className ?? "signup-form"}
        >
            <Typography className="signup-job-title">{jobTitle}</Typography>
            <Grid container spacing={1.5}>
                <Grid item xs={6}>
                    <Controller
                        name={`firstName`}
                        control={control}
                        render={({ field: { ref, ...others } }) => (
                            <InputField
                                {...others}
                                label={"Prénom *"}
                                errortext={errors?.firstName?.message ?? ""}
                                outlinedinputsx={{
                                    ...classes?.contactFormElements
                                        ?.outlinedinputsx,
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Controller
                        name={`lastName`}
                        control={control}
                        render={({ field: { ref, ...others } }) => (
                            <InputField
                                {...others}
                                label={"Nom *"}
                                errortext={errors?.lastName?.message ?? ""}
                                outlinedinputsx={{
                                    ...classes?.contactFormElements
                                        ?.outlinedinputsx,
                                }}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <Controller
                name="email"
                control={control}
                render={({ field: { ref, ...others } }) => (
                    <InputField
                        label={"Adresse email *"}
                        fullWidth
                        {...others}
                        errortext={errors.email?.message ?? ""}
                        outlinedinputsx={{
                            ...classes?.contactFormElements?.outlinedinputsx,
                        }}
                    />
                )}
            />
            <Controller
                name="phoneNumber"
                control={control}
                render={({ field: { ref, ...others } }) => (
                    <InputField
                        label={"Numéro de téléphone *"}
                        fullWidth
                        inputProps={{
                            type: "tel",
                            maxLength: 10,
                        }}
                        {...others}
                        errortext={errors.phoneNumber?.message ?? ""}
                        outlinedinputsx={{
                            ...classes?.contactFormElements?.outlinedinputsx,
                        }}
                    />
                )}
            />

            {formActionComponents.submit}
            {formActionComponents.cancel}
        </form>
    );
}
